import TextEditorCustomize from '@/components/TextEditorCustomize'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import EditIcons from '@/icons/EditIcons'
import PinOffIcons from '@/icons/PinOffIcons'
import { cn } from '@/lib/utils'
import {
    getPinnedCustomerLogs,
    unpinCustomerLog,
    updateCustomerNoteLog,
} from '@/modules/Customer/services'
import { generatePathDatacentral, paths } from '@/utils/path'
import dayjs from 'dayjs'
import { Pin, Server, Sparkles } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { IconButton, Typogaphy } from 'vnetwork-ui'
import { useCustomerReadOnly } from '../../CustomerReadOnlyContext'

// Helper function to extract service info from metadata
const getServiceInfo = (metadata: any) => {
    if (!metadata) return null
    return {
        serviceId: metadata.service_id,
        serviceName: metadata.service_name,
    }
}

const PinnedNotes: React.FC = () => {
    const { id } = useParams()
    const { t } = useTranslation()
    const customerId = Number(id)
    const isReadOnly = useCustomerReadOnly()
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [editingLogId, setEditingLogId] = useState<number | null>(null)
    const [editContent, setEditContent] = useState<{
        text: string
        attachments: any[]
    }>({ text: '', attachments: [] })

    const { data: pinnedLogs = [], renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CUSTOMER_LOGS, 'pinned', customerId],
            queryFn: () => getPinnedCustomerLogs(customerId),
            enabled: !!customerId,
        },
        {
            showLoading: false,
            showError: false,
        }
    )

    const { mutateAsync: unpin } = useMutationWithHandler(
        {
            mutationFn: (logId: number) => unpinCustomerLog(customerId, logId),
        },
        {
            successMessage: t('Note unpinned successfully'),
            reactQueryKeys: [QUERY_KEYS.CUSTOMER_LOGS],
        }
    )

    const { mutateAsync: updateNote, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: ({
                    logId,
                    description,
                }: {
                    logId: number
                    description: string
                }) =>
                    updateCustomerNoteLog(customerId, logId, {
                        note_description: description,
                    }),
            },
            {
                successMessage: t('Note updated successfully'),
                reactQueryKeys: [QUERY_KEYS.CUSTOMER_LOGS],
            }
        )

    const handleEditClick = (logId: number, currentDescription: string) => {
        setEditingLogId(logId)
        setEditContent({ text: currentDescription, attachments: [] })
        setIsEditModalOpen(true)
    }

    const handleCancelEdit = () => {
        setIsEditModalOpen(false)
        setEditingLogId(null)
        setEditContent({ text: '', attachments: [] })
    }

    const handleSaveEdit = async () => {
        if (!editingLogId) return
        try {
            await updateNote({
                logId: editingLogId,
                description: editContent.text,
            })
            setIsEditModalOpen(false)
            setEditingLogId(null)
            setEditContent({ text: '', attachments: [] })
        } catch (error) {
            console.error('Failed to update note:', error)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (pinnedLogs.length === 0) return null

    return (
        <Card className="mt-2 border-1 border-amber-200 dark:border-amber-900/50 bg-gradient-to-br from-amber-50/50 via-white to-orange-50/30 dark:from-amber-950/20 dark:via-background dark:to-orange-950/10 shadow-sm">
            <CardHeader className="pb-2 border-b border-amber-100 dark:border-amber-900/30">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Pin className="h-4 w-4 text-amber-600 dark:text-amber-500 fill-amber-600 dark:fill-amber-500" />
                            <Sparkles className="h-3 w-3 text-amber-400 absolute -top-1 -right-1 animate-pulse" />
                        </div>
                        <CardTitle className="text-base font-medium bg-gradient-to-r from-amber-700 to-orange-600 dark:from-amber-400 dark:to-orange-400 bg-clip-text text-transparent">
                            {t('pinned-notes')}
                        </CardTitle>
                        <Badge
                            variant="outline"
                            className="h-5 px-2 text-xs bg-amber-100 dark:bg-amber-900/40 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 font-semibold"
                        >
                            {pinnedLogs?.length}
                        </Badge>
                    </div>
                </div>
                <CardDescription className="text-xs text-amber-700 dark:text-amber-400/80">
                    {t('pinned-notes-description')}
                </CardDescription>
            </CardHeader>

            <CardContent className="">
                <div className="space-y-2">
                    {pinnedLogs?.map((log, index) => {
                        const serviceInfo = getServiceInfo(
                            log.customer_log.metadata
                        )
                        const isFromService = !!serviceInfo?.serviceName

                        return (
                            <div
                                key={log.customer_log.customer_log_id}
                                className={cn(
                                    'group relative py-2 px-4 rounded-[4px] transition-all duration-300',
                                    // Background colors - blue for service notes, amber for regular notes
                                    isFromService
                                        ? 'bg-gradient-to-br from-white via-blue-50/40 to-cyan-50/30'
                                        : 'bg-gradient-to-br from-white via-amber-50/30 to-orange-50/40',
                                    isFromService
                                        ? 'dark:from-blue-950/20 dark:via-background dark:to-cyan-950/15'
                                        : 'dark:from-amber-950/30 dark:via-background dark:to-orange-950/20',
                                    // Border colors
                                    isFromService
                                        ? 'border-[1.5px] border-blue-200/60 dark:border-blue-800/40'
                                        : 'border-[1.5px] border-amber-200/60 dark:border-amber-800/40',
                                    // Hover border
                                    isFromService
                                        ? 'hover:border-blue-400 dark:hover:border-blue-600'
                                        : 'hover:border-amber-400 dark:hover:border-amber-600',
                                    // Hover shadow
                                    isFromService
                                        ? 'hover:shadow-lg hover:shadow-blue-200/50 dark:hover:shadow-blue-900/30'
                                        : 'hover:shadow-lg hover:shadow-amber-200/50 dark:hover:shadow-amber-900/30',
                                    'hover:-translate-y-0.5',
                                    // Before pseudo element
                                    isFromService
                                        ? 'before:absolute before:inset-0 before:rounded-[4px] before:bg-gradient-to-br before:from-blue-400/0 before:to-cyan-400/0'
                                        : 'before:absolute before:inset-0 before:rounded-[4px] before:bg-gradient-to-br before:from-amber-400/0 before:to-orange-400/0',
                                    'before:opacity-0 hover:before:opacity-10 before:transition-opacity'
                                )}
                                style={{
                                    animationDelay: `${index * 50}ms`,
                                }}
                            >
                                {(() => {
                                    const serviceInfo = getServiceInfo(
                                        log.customer_log.metadata
                                    )
                                    return (
                                        serviceInfo?.serviceName && (
                                            <Link
                                                to={generatePathDatacentral(
                                                    paths.serviceDetail,
                                                    {
                                                        id: serviceInfo.serviceId,
                                                    }
                                                )}
                                                className="flex items-center gap-1.5 pt-1"
                                            >
                                                <Server className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />

                                                <Badge
                                                    variant="outline"
                                                    className="py-0.5 px-4 text-xs bg-blue-50 dark:bg-blue-950/40 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 font-medium hover:underline"
                                                >
                                                    {[
                                                        serviceInfo.serviceId,
                                                        serviceInfo.serviceName,
                                                    ].join(' - ')}
                                                </Badge>
                                            </Link>
                                        )
                                    )
                                })()}
                                <div className="flex items-start justify-between gap-2 relative">
                                    <div className="flex-1 space-y-2">
                                        <div className="relative">
                                            <div
                                                className={cn(
                                                    'absolute -left-2 top-1 w-1 h-full rounded-full opacity-60',
                                                    isFromService
                                                        ? 'bg-gradient-to-b from-blue-400 to-cyan-400 dark:from-blue-500 dark:to-cyan-500'
                                                        : 'bg-gradient-to-b from-amber-400 to-orange-400 dark:from-amber-500 dark:to-orange-500'
                                                )}
                                            />
                                            <div className="space-y-2">
                                                <TextEditorCustomize
                                                    value={{
                                                        text: log.customer_log
                                                            ?.customer_log_description,
                                                        attachments: [],
                                                    }}
                                                    readOnly={true}
                                                    className="border-none bg-transparent"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {!isReadOnly && (
                                        <div className="flex gap-1">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleEditClick(
                                                        log.customer_log
                                                            .customer_log_id,
                                                        log.customer_log
                                                            ?.customer_log_description ||
                                                            ''
                                                    )
                                                }
                                                className="hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all duration-200 hover:scale-110"
                                            >
                                                <EditIcons />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    unpin(
                                                        log.customer_log
                                                            .customer_log_id
                                                    )
                                                }
                                                className="hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all duration-200 hover:scale-110 hover:rotate-12"
                                            >
                                                <PinOffIcons />
                                            </IconButton>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 text-xs justify-end mt-2 pt-2 border-t border-amber-200/40 dark:border-amber-800/40">
                                    <Typogaphy
                                        size="small"
                                        className="text-amber-900 dark:text-amber-100 text-xs font-medium"
                                    >
                                        {log.employee_name}
                                    </Typogaphy>
                                    <Typogaphy
                                        size="small"
                                        className="text-amber-600 dark:text-amber-400"
                                    >
                                        •
                                    </Typogaphy>
                                    <Typogaphy
                                        size="small"
                                        className="text-amber-600 dark:text-amber-400 text-xs"
                                    >
                                        {dayjs(
                                            log.customer_log.created_at
                                        ).format('DD/MM/YYYY HH:mm')}
                                    </Typogaphy>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </CardContent>

            {/* Edit Modal */}
            <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{t('Edit Note')}</DialogTitle>
                        <DialogDescription>
                            {t(
                                'Make changes to your note here. Click save when you are done.'
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <TextEditorCustomize
                            value={editContent}
                            onChange={(value) => setEditContent(value)}
                            className="min-h-[300px]"
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleCancelEdit}
                            disabled={isUpdating}
                        >
                            {t('Cancel')}
                        </Button>
                        <Button onClick={handleSaveEdit} disabled={isUpdating}>
                            {isUpdating ? t('Saving...') : t('Save changes')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    )
}

export default PinnedNotes
